import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FaUserSecret, FaFilter, FaTimes, FaEye, FaLock, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import { MdAccessTime } from 'react-icons/md';
import toast from 'react-hot-toast';
import saApi from '../../services/superAdminApi';

// ── constants ──────────────────────────────────────────────────────────────────

// Tinted pills matching the club-side Support Access ledger: muted read-only, red-tint full access.
const MODE_STYLE = {
  readonly:    { label: 'Read-Only',   bg: 'rgba(107,101,96,0.2)',   color: '#8a847c', icon: FaEye  },
  full_access: { label: 'Full Access', bg: 'rgba(248,113,113,0.12)', color: '#f87171', icon: FaLock },
};

// Active = green, Ended = muted, Expired = amber (a lapsed session isn't an alarm, not bright red).
const STATUS_STYLE = {
  active:  { bg: 'rgba(52,211,153,0.12)',  color: '#34d399' },
  ended:   { bg: 'rgba(107,101,96,0.2)',   color: '#8a847c' },
  expired: { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
};

const CLUB_STATUS_COLORS = {
  trial:         '#60a5fa',
  trialing:      '#60a5fa',
  active:        '#34d399',
  suspended:     '#f87171',
  trial_expired: '#8a847c',
  cancelled:     '#8a847c',
  archived:      '#8a847c',
};

// ── helpers ────────────────────────────────────────────────────────────────────

const formatDuration = (startedAt, endedAt) => {
  if (!endedAt) return '—';
  const mins = Math.round((new Date(endedAt) - new Date(startedAt)) / 60000);
  if (mins < 1)  return '<1m';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const th = {
  fontSize: 11, fontWeight: 700, color: '#6b6560', textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 14px', background: '#1a1918',
};
const cell = {
  fontSize: 13, padding: '12px 14px', borderBottom: '1px solid rgba(237,232,224,0.05)',
  color: '#c5bfb5', verticalAlign: 'top',
};

// ── StartImpersonationPanel ────────────────────────────────────────────────────

function StartImpersonationPanel({ onStarted, prefill }) {
  const [clubSearch,   setClubSearch]   = useState('');
  const [selected,     setSelected]     = useState(null);
  const [mode,         setMode]         = useState('readonly');
  const [reason,       setReason]       = useState('');
  const [faConfirmed,  setFaConfirmed]  = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [focused,      setFocused]      = useState(false);
  const searchRef = useRef(null);
  const reasonRef = useRef(null);

  // Quick re-impersonate from a history row: prefill club + mode, focus reason.
  // Reason is never prefilled — it must be typed fresh (≥10 chars).
  useEffect(() => {
    if (!prefill) return;
    setSelected({ _id: prefill.clubId, name: prefill.clubName });
    setClubSearch(prefill.clubName);
    setMode(prefill.mode || 'readonly');
    setFaConfirmed(false);
    setReason('');
    reasonRef.current?.focus();
  }, [prefill]);

  const { data: searchData } = useQuery({
    queryKey: ['sa-club-search', clubSearch],
    queryFn:  () => saApi.get('/api/super-admin/clubs', {
      params: { search: clubSearch, limit: 20 },
    }).then(r => r.data),
    // Fetch when the field is focused (even empty → browse the full short list) or while typing.
    enabled: !selected && (focused || clubSearch.trim().length >= 1),
    staleTime: 10_000,
  });

  const clubs = searchData?.clubs || [];

  const handleSelect = (club) => {
    setSelected(club);
    setClubSearch(club.name);
  };

  const handleStart = async () => {
    if (!selected) { toast.error('Select a club first'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (reason.trim().length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    if (mode === 'full_access' && !faConfirmed) {
      toast.error('Please confirm you understand the full-access implications');
      return;
    }

    setLoading(true);
    try {
      const { data } = await saApi.post('/api/super-admin/impersonation/start', {
        clubId: selected._id, reason: reason.trim(), mode,
      });

      localStorage.setItem('trainaxis_imp_session', JSON.stringify({
        sessionId: data.session.id,
        clubName:  data.club.name,
        clubSlug:  data.club.slug,
        expiresAt: data.session.expiresAt,
        mode:      data.session.mode,
        reason:    reason.trim(),
      }));
      // Store impersonation token as the active club auth token.
      // Also store the admin user object so AuthContext restores a valid session
      // on page reload without asking for login credentials.
      localStorage.setItem('trainaxis_token',     data.token);
      localStorage.setItem('trainaxis_user',      JSON.stringify(data.adminUser));
      localStorage.setItem('trainaxis_club_slug', data.club.slug);

      toast.success(`Entering ${data.club.name} as ${mode === 'readonly' ? 'read-only observer' : 'full-access admin'}…`);
      if (onStarted) onStarted();
      window.location.href = '/admin/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start impersonation');
    } finally {
      setLoading(false);
    }
  };

  const showDropdown = focused && !selected && clubs.length > 0;

  return (
    <div className="rounded-2xl border p-6 space-y-4" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
      <div>
        <h2 className="text-base font-bold text-[#ede8e0] flex items-center gap-2">
          <FaUserSecret className="text-[#c8102e]" /> Start Impersonation Session
        </h2>
        <p className="text-xs mt-1" style={{ color: '#8a847c' }}>
          Opens the club's admin portal as that club's admin. Every session is logged.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Club search */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#8a847c' }}>
            Club *
          </label>
          <div className="relative" ref={searchRef}>
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <FaSearch style={{ color: '#6b6560', fontSize: 13 }} />
            </div>
            <input
              value={clubSearch}
              onChange={e => { setClubSearch(e.target.value); setSelected(null); }}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
              placeholder="Search or click to browse clubs…"
              className="w-full border rounded-full pl-9 pr-4 py-2.5 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560]"
              style={{ background: '#1a1918', borderColor: selected ? '#16a34a' : 'rgba(237,232,224,0.08)' }}
            />
            {selected && (
              <button
                onClick={() => { setSelected(null); setClubSearch(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#8a847c' }}>
                <FaTimes />
              </button>
            )}
            {showDropdown && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border shadow-xl overflow-hidden"
                style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
                {clubs.map(club => (
                  <button
                    key={club._id}
                    type="button"
                    // Prevent the mousedown from blurring the search input — otherwise onBlur
                    // closes the dropdown before this option's click can register (the row
                    // would unmount mid-click and selection silently fails).
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelect(club)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[#28261f] transition-colors">
                    <div>
                      <div className="text-sm font-medium text-[#ede8e0]">{club.name}</div>
                      <div className="text-xs" style={{ color: '#8a847c' }}>{club.slug}.trainaxis.com</div>
                    </div>
                    <span className="text-[10px] font-semibold capitalize px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0"
                      style={{ background: `${CLUB_STATUS_COLORS[club.status] || '#8a847c'}22`, color: CLUB_STATUS_COLORS[club.status] || '#8a847c' }}>
                      {club.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selected && (
            <div className="mt-1.5 flex items-center gap-2 text-xs" style={{ color: '#4ade80' }}>
              <span className="font-semibold">✓ {selected.name}</span>
              {(selected.slug || selected.status) && (
                <span style={{ color: '#8a847c' }}>
                  {selected.slug ? `${selected.slug}.trainaxis.com` : ''}
                  {selected.slug && selected.status ? ' · ' : ''}
                  {selected.status || ''}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Mode */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#8a847c' }}>
            Mode *
          </label>
          <div className="flex gap-2">
            {[
              { value: 'readonly',    label: 'Read-Only',   icon: FaEye,  desc: 'View only, no changes' },
              { value: 'full_access', label: 'Full Access', icon: FaLock, desc: 'Can make changes (logged)' },
            ].map(opt => {
              const Icon = opt.icon;
              const active = mode === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => { setMode(opt.value); setFaConfirmed(false); }}
                  className="flex-1 flex flex-col items-start gap-1 rounded-xl border px-3 py-2.5 text-left transition-all"
                  style={{
                    background:   active ? (opt.value === 'full_access' ? 'rgba(200,16,46,0.14)' : 'rgba(96,165,250,0.12)') : '#1a1918',
                    borderColor:  active ? (opt.value === 'full_access' ? '#c8102e' : '#60a5fa') : 'rgba(237,232,224,0.08)',
                    color:        active ? (opt.value === 'full_access' ? '#c8102e' : '#60a5fa')  : '#8a847c',
                  }}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold">
                    <Icon className="text-xs" />
                    {opt.label}
                  </div>
                  <div className="text-xs" style={{ color: active ? 'inherit' : '#6b6560', opacity: 0.8 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#8a847c' }}>
            Reason * <span className="normal-case font-normal text-[#6b6560]">(logged)</span>
          </label>
          <textarea
            ref={reasonRef}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Investigating payment failure reported by club owner…"
            rows={3}
            className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] resize-none"
            style={{ background: '#1a1918', borderColor: reason.trim() ? 'rgba(237,232,224,0.08)' : (loading ? '#c8102e' : 'rgba(237,232,224,0.08)') }}
          />
          {reason.trim().length > 0 && reason.trim().length < 10 && (
            <p className="text-xs mt-1.5" style={{ color: '#8a847c' }}>
              {10 - reason.trim().length} more character{10 - reason.trim().length === 1 ? '' : 's'} needed
            </p>
          )}
        </div>
      </div>

      {mode === 'full_access' && (
        <div className="rounded-xl px-4 py-3 space-y-3"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <div className="flex items-start gap-2.5 text-xs" style={{ color: '#fca5a5' }}>
            <FaExclamationTriangle className="flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p><strong>Full Access</strong> allows changes inside this club account.</p>
              <p>Use only when necessary. Every action is logged and visible in the club's Support Access History.</p>
              <p>Changes will appear under the club admin's name in their activity history.</p>
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={faConfirmed}
              onChange={e => setFaConfirmed(e.target.checked)}
              className="w-4 h-4 accent-red-500 cursor-pointer"
            />
            <span className="text-xs font-semibold" style={{ color: '#fca5a5' }}>
              I understand this session can make real changes to club data.
            </span>
          </label>
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={loading || !selected || reason.trim().length < 10 || (mode === 'full_access' && !faConfirmed)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:bg-[#e01535] disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: '#c8102e', color: '#fff' }}>
        <FaUserSecret />
        {loading ? 'Starting…' : 'Start Session'}
      </button>
    </div>
  );
}

// ── History table ──────────────────────────────────────────────────────────────

function HistoryTable({ onReImpersonate }) {
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modeFilter,   setModeFilter]   = useState('all');
  const [showFilters,  setShowFilters]  = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['sa-imp-history'],
    queryFn:  () => saApi.get('/api/super-admin/impersonation/history').then(r => r.data),
  });

  const sessions = data?.sessions || [];

  const filtered = useMemo(() => {
    return sessions.filter(s => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (modeFilter   !== 'all' && s.mode   !== modeFilter)   return false;
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (new Date(s.startedAt) < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(s.startedAt) > to) return false;
      }
      return true;
    });
  }, [sessions, statusFilter, modeFilter, dateFrom, dateTo]);

  const hasFilters = statusFilter !== 'all' || modeFilter !== 'all' || dateFrom || dateTo;

  const selectStyle = {
    background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)', borderRadius: 999,
    padding: '6px 10px', color: '#c5bfb5', fontSize: 12, outline: 'none',
  };
  const inputStyle = { ...selectStyle };

  return (
    <div className="space-y-4">
      {/* Sub-header + filter toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-[#ede8e0]">Session History</h2>
          <p className="text-xs mt-0.5" style={{ color: '#8a847c' }}>
            {sessions.length} total session{sessions.length !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-colors"
          style={{
            background: hasFilters ? 'rgba(200,16,46,0.14)' : '#1a1918',
            color:      hasFilters ? '#c8102e' : '#c5bfb5',
            border:     `1px solid ${hasFilters ? 'rgba(200,16,46,0.3)' : 'rgba(237,232,224,0.10)'}`,
          }}>
          <FaFilter className="text-xs" />
          Filters {hasFilters && `(${[statusFilter !== 'all', modeFilter !== 'all', !!dateFrom, !!dateTo].filter(Boolean).length})`}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="rounded-2xl p-4 border flex flex-wrap gap-4 items-end"
          style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <div>
            <label className="block text-xs font-semibold text-[#6b6560] mb-1.5 uppercase tracking-wider">Status</label>
            <select style={selectStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="ended">Ended</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b6560] mb-1.5 uppercase tracking-wider">Mode</label>
            <select style={selectStyle} value={modeFilter} onChange={e => setModeFilter(e.target.value)}>
              <option value="all">All modes</option>
              <option value="readonly">Read-Only</option>
              <option value="full_access">Full Access</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b6560] mb-1.5 uppercase tracking-wider">From</label>
            <input type="date" style={inputStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b6560] mb-1.5 uppercase tracking-wider">To</label>
            <input type="date" style={inputStyle} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          {hasFilters && (
            <button
              onClick={() => { setStatusFilter('all'); setModeFilter('all'); setDateFrom(''); setDateTo(''); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium"
              style={{ background: '#28261f', color: '#c5bfb5' }}>
              <FaTimes className="text-xs" /> Clear All
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }}>
        {isPending ? (
          <p className="p-8 text-center text-sm" style={{ color: '#6b6560' }}>Loading sessions…</p>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FaUserSecret className="mx-auto text-3xl mb-3" style={{ color: '#6b6560' }} />
            <p className="text-sm font-medium text-[#8a847c]">
              {hasFilters ? 'No sessions match your filters' : 'No impersonation sessions recorded'}
            </p>
          </div>
        ) : (
          <table className="w-full border-collapse min-w-[700px]">
            <thead>
              <tr>
                {['Admin', 'Club', 'Mode', 'Started', 'Duration', 'Reason', 'Status', ''].map((h, i) => (
                  <th key={h || `col-${i}`} style={th} className="text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const modeStyle   = MODE_STYLE[s.mode]   || MODE_STYLE.readonly;
                const statusStyle = STATUS_STYLE[s.status] || STATUS_STYLE.ended;
                const ModeIcon    = modeStyle.icon;
                return (
                  <tr key={s._id} className="hover:bg-[#28261f] transition-colors">
                    <td style={cell}>
                      <p className="font-medium text-[#ede8e0]">{s.superAdminName}</p>
                      {s.superAdminRole && (
                        <p className="text-xs mt-0.5 capitalize" style={{ color: '#8a847c' }}>{s.superAdminRole}</p>
                      )}
                    </td>
                    <td style={cell}>
                      <p className="font-medium" style={{ color: '#ede8e0' }}>{s.targetClubName || '—'}</p>
                    </td>
                    <td style={cell}>
                      <span className="flex items-center gap-1.5 text-xs font-semibold w-fit px-2 py-0.5 rounded-full"
                        style={{ background: modeStyle.bg, color: modeStyle.color }}>
                        <ModeIcon className="text-xs" />
                        {modeStyle.label}
                      </span>
                    </td>
                    <td style={cell}>
                      <p className="text-sm" style={{ color: '#c5bfb5' }}>{fmtDateTime(s.startedAt)}</p>
                      {s.endedAt && (
                        <p className="text-xs mt-0.5" style={{ color: '#8a847c' }}>Ended {fmtDateTime(s.endedAt)}</p>
                      )}
                    </td>
                    <td style={cell}>
                      <div className="flex items-center gap-1.5">
                        <MdAccessTime className="text-[#6b6560] flex-shrink-0" />
                        <span style={{ color: s.endedAt ? '#c5bfb5' : '#8a847c' }}>
                          {formatDuration(s.startedAt, s.endedAt)}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...cell, maxWidth: 240 }}>
                      <p className="text-xs leading-relaxed" style={{ color: '#8a847c' }}>
                        {s.reason || '—'}
                      </p>
                    </td>
                    <td style={cell}>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize"
                        style={{ background: statusStyle.bg, color: statusStyle.color }}>
                        {s.status}
                      </span>
                    </td>
                    <td style={cell}>
                      <button
                        onClick={() => onReImpersonate(s)}
                        title="Prefill the form to impersonate this club again"
                        className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border transition-colors"
                        style={{ background: 'transparent', borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#28261f')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        Start again
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!isPending && filtered.length > 0 && (
        <p className="text-xs text-right" style={{ color: '#6b6560' }}>
          Showing {filtered.length} of {sessions.length} sessions
        </p>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ImpersonationPage() {
  const qc = useQueryClient();
  // Re-impersonate request from a history row → prefill the form. Nonce re-triggers on repeat clicks.
  const [prefill, setPrefill] = useState(null);
  const reImpersonate = (session) => setPrefill(prev => ({
    clubId:   session.targetClubId,
    clubName: session.targetClubName,
    mode:     session.mode,
    nonce:    (prev?.nonce || 0) + 1,
  }));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[#ede8e0] flex items-center gap-2">
          <FaUserSecret className="text-[#c8102e]" /> Club Impersonation
        </h1>
        <p className="text-sm mt-1" style={{ color: '#8a847c' }}>
          Access any club's admin portal as a superadmin observer. All sessions are logged.
        </p>
      </div>

      <StartImpersonationPanel prefill={prefill} onStarted={() => qc.invalidateQueries({ queryKey: ['sa-imp-history'] })} />
      <HistoryTable onReImpersonate={reImpersonate} />
    </div>
  );
}
